import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import disableAttachmentPreview from 'owa-attachment-data/lib/actions/disableAttachmentPreview';
import {
    AttachmentFullViewState,
    AttachmentMenuAction,
    createAttachmentFullViewStateAndStoreBackingModel,
    createInlineAttachmentState,
    getAttachmentSharingInformationForODB,
    getDefaultSupportedMenuAction,
    InlineAttachmentStatus,
} from 'owa-attachment-full-data';
import { ReferenceAttachmentModel } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import shouldShowImageView from 'owa-attachment-model-store/lib/utils/shouldShowImageView';
import { ClientAttachmentId, MailboxInfo } from 'owa-client-ids';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ItemAttachment from 'owa-service/lib/contract/ItemAttachment';
import ItemId from 'owa-service/lib/contract/ItemId';
import isSmimeDataPackage from 'owa-smime-adapter/lib/utils/isSmimeDataPackage';
import datapoints from '../../datapoints';
import AttachmentWellViewState from '../../schema/AttachmentWellViewState';
import ConversationAttachmentWellViewState from '../../schema/ConversationAttachmentWellViewState';
import { referenceAttachmentType } from '../../utils/attachmentConstants';
import changeTotalClassicAttachmentSize from '../changeTotalClassicAttachmentSize';
import { trace } from 'owa-trace';

/**
 * Stores the attachment in the mail store and initializes the attachment view state for all the attachments
 * @param attachment - the attachment to be hosted in the attachment well
 * @param mailboxInfo - the mailbox info used for the client attachment id
 * @param attachmentWell - the view state for this attachment well
 * @param forceUpdateAttachmentsInStore - whether to update the existing attachment in the store
 * @param supportedMenuActions - The list of supported menu actions for the attachment
 * @param isInitializationForCompose - boolean to indicate if this action is called for compose scenario in mail
 */
function storeAndInitializeAttachment(
    attachment: AttachmentType,
    mailboxInfo: MailboxInfo,
    attachmentWell: AttachmentWellViewState,
    forceUpdateAttachmentsInStore: boolean,
    isInitializationForCompose: boolean,
    supportedMenuActions: AttachmentMenuAction[],
    parentItemId: ItemId,
    isSMIMEItem: boolean
): void {
    // Since S/MIME users can toggle to S/MIME scenario anytime from Compose, we need to ensure that we keep the inline attachments
    // in the attachmentWell
    // Since the attachmentWell needs to contain inline Attachments for add-in Attachment Apis, we need to ensure we keep inline attachments in attachmentWell

    // sharing metadata attachments should not be shown in native mail
    if (attachment.ContentType === 'application/x-sharing-metadata-xml') {
        return;
    }

    // Hide p7m file from the well
    if (isSMIMEItem && isSmimeDataPackage(attachment)) {
        return;
    }

    const oneAttachment: AttachmentFullViewState = createAttachmentFullViewStateAndStoreBackingModel(
        { ...attachment.AttachmentId, mailboxInfo: mailboxInfo },
        attachment,
        attachmentWell.isReadOnly,
        true,
        forceUpdateAttachmentsInStore,
        isSMIMEItem,
        null,
        supportedMenuActions,
        true
    );
    const isConversationAttachmentWell = !!(attachmentWell as ConversationAttachmentWellViewState)
        .parentItemIdMap;
    if (attachment.IsInline && attachment.__type !== referenceAttachmentType) {
        // Since onSmimeOptionsChangeOrchestrator takes action on all attachments in the well, we ensure to push inline attachments.
        // The inline image in this scenario need not be processed again, hence setting the status to completed
        attachmentWell.inlineAttachments.push(
            createInlineAttachmentState(
                attachment.AttachmentId as ClientAttachmentId,
                attachment.AttachmentId.Id,
                true /* isHiddenInline */,
                InlineAttachmentStatus.Completed
            )
        );
    } else if (shouldShowImageView(attachment) && !isConversationAttachmentWell) {
        attachmentWell.imageViewAttachments.push(oneAttachment);
    } else {
        attachmentWell.docViewAttachments.push(oneAttachment);
    }

    if (!oneAttachment.isCloudy) {
        changeTotalClassicAttachmentSize(attachmentWell, attachment.Size, attachment.AttachmentId);
    } else {
        const attachmentModel: ReferenceAttachmentModel = <ReferenceAttachmentModel>(
            getAttachment(oneAttachment.attachmentId)
        );
        if (attachmentModel.requiresFetchingSharingInformation && parentItemId) {
            getAttachmentSharingInformationForODB(attachmentModel);
        }
    }
}

/**
 * Initializes the attachment well
 * @param attachments - the attachments to be hosted in the attachment well
 * @param mailboxInfo - the mailbox info used for the client attachment id
 * @param attachmentWell - the view state for this attachment well
 * @param forceUpdateAttachmentsInStore - whether to update the existing attachment in the store
 * @param unsupportedMenuActions - The list of unsupported menu actions for the attachments
 */
export default createLegacyActionWithDatapoint(
    'initializeAttachments',
    datapoints.AttachmentWellInitialization
)(function initializeAttachments(
    attachments: AttachmentType[],
    mailboxInfo: MailboxInfo,
    attachmentWell: AttachmentWellViewState,
    forceUpdateAttachmentsInStore: boolean,
    isInitializationForCompose: boolean,
    unsupportedMenuActions?: AttachmentMenuAction[],
    parentItemId?: ItemId,
    isMessageEncrypted?: boolean,
    isSMIMEMessage?: boolean
): void {
    // If attachments is null, we don't need to do anything
    if (!attachments) {
        return;
    }

    // TODO: VSO# 56387: Remove the following once we found the root cause
    const attachmentsWithNoName = attachments.filter(att => !att.Name);
    if (attachmentsWithNoName.length > 0) {
        const attachmentIdsStr = attachmentsWithNoName
            .map(att => (att.AttachmentId ? att.AttachmentId.Id : 'UNDEFINED'))
            .join(',');
        trace.error({
            error: new Error(
                `[InitializeAttachments] Attachments found with no name. Ids: ${attachmentIdsStr}`
            ),
        });
    }

    const supportedMenuActions = getSupportedMenuActions(unsupportedMenuActions);

    // disable preview for calendar item attachment encrypted with IRM
    if (isMessageEncrypted) {
        if (attachmentWell.docViewAttachments.length > 0) {
            attachmentWell.docViewAttachments.forEach(docAttachment => {
                if (isCalendarItemAttachment(docAttachment.attachmentId)) {
                    disableAttachmentPreview(docAttachment.strategy);
                }
            });
        }
    }

    if (!attachmentWell.isInitialized) {
        attachments.forEach(attachment => {
            storeAndInitializeAttachment(
                attachment,
                mailboxInfo,
                attachmentWell,
                forceUpdateAttachmentsInStore,
                isInitializationForCompose,
                supportedMenuActions,
                parentItemId,
                isSMIMEMessage
            );
        });
    }

    attachmentWell.supportedMenuActions = supportedMenuActions;
    attachmentWell.isInitialized = true;
});

function getSupportedMenuActions(
    unsupportedMenuActions?: AttachmentMenuAction[]
): AttachmentMenuAction[] | null {
    const supportedMenuActions = unsupportedMenuActions
        ? getDefaultSupportedMenuAction().filter(action => !unsupportedMenuActions.includes(action))
        : null;
    return supportedMenuActions;
}

function isCalendarItemAttachment(attachmentId: ClientAttachmentId): boolean {
    const attachmentModel = getAttachment(attachmentId);
    if (attachmentModel && attachmentModel.model) {
        const embeddedItemClass = (attachmentModel.model as ItemAttachment).EmbeddedItemClass;
        return embeddedItemClass === 'IPM.Appointment';
    }
    return false;
}
